import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Tabs,
  Tab,
} from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { teal, green } from '@material-ui/core/colors';

import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';
import api from '../../services/api';
import usePipelines from '../../hooks/usePipelines';
import DealActivityHistory from '../DealActivityHistory';
import OrderModal from '../orderModal';
import FileLinksModal from '../FileLinksModal';
import GoogleScheduleModal from '../GoogleScheduleModal';

const useStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  stageChip: {
    marginLeft: theme.spacing(1),
  },
  categoryTitle: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  activityItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  completedActivity: {
    backgroundColor: theme.palette.type === 'dark' ? teal[900] : green[50],
    borderColor: theme.palette.type === 'dark' ? teal[600] : green[200],
    '& .MuiListItemText-primary': {
      color:
        theme.palette.type === 'dark' ? teal[100] : theme.palette.text.primary,
    },
    '& .MuiListItemText-secondary': {
      color:
        theme.palette.type === 'dark'
          ? teal[200]
          : theme.palette.text.secondary,
    },
  },
  activityDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  activityField: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  requiredField: {
    '& .MuiInputLabel-root': {
      '&::after': {
        content: '" *"',
        color: theme.palette.error.main,
      },
    },
  },
  saveButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
}));

const formatDealDateForHistory = deal => {
  const dateStr = deal.createdAt || deal.updatedAt;
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return Number.isFinite(date.getTime())
      ? date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '';
  } catch {
    return '';
  }
};

const getDealStatusLabel = deal => {
  if (deal.status === 'closed') return 'Concluído';
  if (deal.status === 'lost') return 'Perdido';
  return 'Em andamento';
};

const StageActivitiesModal = ({ open, onClose, ticket, onActivitiesSaved }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { findAll: findAllPipelines, findById } = usePipelines();
  const [stageData, setStageData] = useState(null);
  const [stageActivities, setStageActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Espelho de pendingChanges para ser lido dentro de loadActivityResponses.
  // Não dá para colocar pendingChanges nas dependências do useCallback: ele é
  // chamado de vários efeitos, e mudar a identidade da função a cada digitação
  // dispararia recarga em loop.
  const pendingChangesRef = useRef({});

  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  // Estados para controlar abertura dos modais
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [googleScheduleModalOpen, setGoogleScheduleModalOpen] = useState(false);
  const [scheduleIdToEdit, setScheduleIdToEdit] = useState(null);
  const [fileLinksModalOpen, setFileLinksModalOpen] = useState(false);
  const [fileLinksDealId, setFileLinksDealId] = useState(null);
  const [scheduleQueue, setScheduleQueue] = useState([]);

  // Estado para controlar se o estágio foi concluído
  const [stageCompleted, setStageCompleted] = useState(false);

  // Abas: 0 = Atividades do estágio, 1 = Atividades realizadas
  const [tabValue, setTabValue] = useState(0);
  // ID da atividade em edição na aba "Atividades realizadas"
  const [editingActivityId, setEditingActivityId] = useState(null);
  // Dados da aba "Atividades realizadas" (GET /deals/:id/requirements-with-filling)
  const [completedActivitiesList, setCompletedActivitiesList] = useState([]);
  const [loadingCompletedActivities, setLoadingCompletedActivities] =
    useState(false);

  // Estados para Histórico de Atendimentos (igual CrmList)
  const [contactDealsFromAllPipelines, setContactDealsFromAllPipelines] =
    useState([]);
  const [stagesForActivityHistory, setStagesForActivityHistory] = useState([]);
  const [selectedDealIdForHistory, setSelectedDealIdForHistory] =
    useState(null);
  const [loadingContactDealsAllPipelines, setLoadingContactDealsAllPipelines] =
    useState(false);

  // Função para obter o deal mais recente (ID mais alto)
  const getCurrentDeal = useCallback(() => {
    if (!ticket) return null;

    // Se o ticket estiver fechado, não retornar deal
    if (ticket.status === 'closed') return null;

    // Se o ticket estiver open, retornar o último deal (mais recente)
    if (ticket.status === 'open') {
      if (!ticket.deals || ticket.deals.length === 0) return null;

      // Retornar o deal com maior ID (mais recente)
      return ticket.deals.reduce((latest, current) =>
        current.id > latest.id ? current : latest
      );
    }

    return null;
  }, [ticket]);

  const loadStageData = useCallback(async () => {
    setLoading(true);
    try {
      // Obter informações do estágio do deal ativo (ticket pode ter vários deals, apenas um ativo)
      const currentDeal = getCurrentDeal();
      const stageData = currentDeal?.stage ?? ticket?.deal?.stage ?? null;

      if (!stageData) {
        throw new Error('Estágio não encontrado para este ticket');
      }

      setStageData({
        id: stageData.id,
        name: stageData.name,
        color: stageData.color,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [ticket, getCurrentDeal]);

  const loadActivityResponses = useCallback(async () => {
    try {
      // Buscar respostas das atividades da API usando o deal ativo (um ticket pode ter vários deals, mas apenas um ativo)
      const currentDeal = getCurrentDeal();
      const dealId =
        currentDeal?.id ?? ticket?.dealId ?? ticket?.deal?.id ?? null;
      if (dealId) {
        const { data } = await api.get(`/deals/${dealId}/requirements`);

        // Usar diretamente os requirements da API
        const requirements = data.requirements || [];

        // O que o usuário já preencheu mas ainda não salvou tem precedência
        // sobre o valor do servidor. Sem isso, qualquer recarga desta função
        // (fechar o modal de anexos, de venda ou de agendamento) apagava a
        // resposta em edição — o preenchimento só vai para a API no
        // "Salvar alterações", então o servidor ainda devolve o valor antigo.
        const pending = pendingChangesRef.current || {};

        // Converter requirements em atividades
        const activities = requirements.map(req => {
          const hasPending = Object.prototype.hasOwnProperty.call(
            pending,
            req.id
          );

          let activity = {
            id: req.id,
            descricao: req.name,
            obrigatorio: req.required,
            order: req.order,
            filled: req.filled || false,
            value: hasPending
              ? pending[req.id]
              : req.value || (req.type === 'checkbox' ? false : ''),
            type: req.type,
          };

          // Mapear tipos de requirements para tipos de atividades
          switch (req.type) {
            case 'checkbox':
              activity.tipo_gatilho = 'checkbox';
              break;
            case 'text':
              activity.tipo_gatilho = 'input';
              break;
            case 'select':
              activity.tipo_gatilho = 'select';
              activity.opcoes = req.options || [];
              break;
            case 'date':
              activity.tipo_gatilho = 'date';
              break;
            case 'sale':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Venda realizada`;
              break;
            case 'attach':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Links anexados`;
              break;
            case 'schedule':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Data agendada`;
              break;
            default:
              activity.tipo_gatilho = 'input';
          }

          return activity;
        });

        // Ordenar por ordem definida
        activities.sort((a, b) => a.order - b.order);

        setStageActivities(activities);
      }
    } catch (err) {
      console.warn('Erro ao carregar requirements:', err);
      setStageActivities([]);
    }
  }, [ticket, getCurrentDeal]);

  /**
   * Mescla agendamentos de scheduleActivities (schedule_created) com req.schedules.
   * Ignora agendamentos cancelados (schedule_cancelled) e deduplica por id.
   */
  const mergeSchedulesFromActivities = (schedules, scheduleActivities) => {
    const idKey = id => String(id ?? '');
    const cancelledIds = new Set(
      (scheduleActivities || [])
        .filter(a => a.type === 'schedule_cancelled' && a.data?.calendarId)
        .map(a => idKey(a.data.calendarId))
    );

    const byId = new Map();
    for (const s of schedules || []) {
      const key = idKey(s.id);
      const isCancelled =
        cancelledIds.has(key) ||
        String(s.status || '').toLowerCase() === 'cancelled';
      if (key && !isCancelled && !byId.has(key)) {
        byId.set(key, s);
      }
    }

    const fromActivities = (scheduleActivities || [])
      .filter(
        a =>
          a.type === 'schedule_created' &&
          a.data?.calendarId &&
          !cancelledIds.has(idKey(a.data.calendarId))
      )
      .map(a => ({
        id: a.data.calendarId,
        title: a.data.title,
        startDateTime: a.data.startDateTime,
        endDateTime: a.data.endDateTime,
        location: a.data.location,
        productId: a.data.productId,
        productName: a.data.productName,
      }));

    for (const s of fromActivities) {
      const key = idKey(s.id);
      if (key && !byId.has(key)) {
        byId.set(key, s);
      }
    }

    return Array.from(byId.values()).sort(
      (a, b) =>
        new Date(a.startDateTime || 0) - new Date(b.startDateTime || 0)
    );
  };

  // Buscar requirements com preenchimento para a aba "Atividades realizadas" (GET /deals/:id/requirements-with-filling)
  const loadRequirementsWithFilling = useCallback(async () => {
    const currentDeal = getCurrentDeal();
    const dealId =
      currentDeal?.id ?? ticket?.dealId ?? ticket?.deal?.id ?? null;
    if (!dealId) {
      setCompletedActivitiesList([]);
      return;
    }
    setLoadingCompletedActivities(true);
    try {
      const { data } = await api.get(
        `/deals/${dealId}/requirements-with-filling`
      );
      const requirements = data.requirements || [];
      // Incluir requirements que tenham filling OU valor preenchido (a API pode não enviar "filled")
      const activities = requirements
        .filter(
          req =>
            req.filling != null ||
            req.filled === true ||
            (req.value !== undefined &&
              req.value !== null &&
              req.value !== '' &&
              req.value !== false)
        )
        .map(req => {
          const schedules =
            req.type === 'schedule'
              ? mergeSchedulesFromActivities(
                  req.schedules,
                  req.scheduleActivities ?? data.scheduleActivities
                )
              : req.schedules || [];

          let activity = {
            id: req.id,
            descricao: req.name,
            obrigatorio: req.required,
            order: req.order,
            filled: req.filled !== false || req.filling != null,
            value: req.value ?? (req.type === 'checkbox' ? false : ''),
            type: req.type,
            filling: req.filling || null,
            stageName: req.stageName || null,
            saleItems: req.saleItems || [],
            scheduleActivities: req.scheduleActivities || [],
            schedules,
          };
          switch (req.type) {
            case 'checkbox':
              activity.tipo_gatilho = 'checkbox';
              break;
            case 'text':
              activity.tipo_gatilho = 'input';
              break;
            case 'select':
              activity.tipo_gatilho = 'select';
              activity.opcoes = req.options || [];
              break;
            case 'date':
              activity.tipo_gatilho = 'date';
              break;
            case 'sale':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Venda realizada`;
              break;
            case 'attach':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Links anexados`;
              break;
            case 'schedule':
              activity.tipo_gatilho = 'indicator';
              activity.descricao = `${req.name} - Data agendada`;
              break;
            default:
              activity.tipo_gatilho = 'input';
          }
          return activity;
        })
        .filter(
          a =>
            a.type !== 'schedule' ||
            (a.schedules && a.schedules.length > 0)
        );
      activities.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCompletedActivitiesList(activities);
    } catch (err) {
      console.warn('Erro ao carregar requirements-with-filling:', err);
      setCompletedActivitiesList([]);
    } finally {
      setLoadingCompletedActivities(false);
    }
  }, [ticket, getCurrentDeal]);

  // Buscar dados do estágio e respostas das atividades
  useEffect(() => {
    if (open) {
      loadStageData();
      loadActivityResponses();
      loadRequirementsWithFilling();
    }
  }, [
    open,
    ticket,
    loadStageData,
    loadActivityResponses,
    loadRequirementsWithFilling,
  ]);

  // Buscar todos os deals do contato em todas as pipelines (igual CrmList)
  const fetchContactDealsFromAllPipelines = useCallback(
    async contactId => {
      if (!contactId) {
        setContactDealsFromAllPipelines([]);
        setStagesForActivityHistory([]);
        return;
      }
      setLoadingContactDealsAllPipelines(true);
      setContactDealsFromAllPipelines([]);
      setStagesForActivityHistory([]);
      try {
        const pipelines = await findAllPipelines();
        if (!Array.isArray(pipelines) || pipelines.length === 0) {
          setLoadingContactDealsAllPipelines(false);
          return;
        }
        const allStages = [];
        pipelines.forEach(p => {
          if (p?.stages && Array.isArray(p.stages)) {
            p.stages.forEach(stage => {
              allStages.push({
                ...stage,
                pipelineId: p.id,
                pipelineName: p.name || '-',
              });
            });
          }
        });
        setStagesForActivityHistory(allStages);

        const pipelinesWithId = pipelines.filter(p => p?.id);
        const results = await Promise.all(
          pipelinesWithId.map(p =>
            findById(p.id, { params: { contactId } }).catch(() => null)
          )
        );

        const allDeals = [];
        results.forEach((pipelineData, idx) => {
          if (!pipelineData?.stages) return;
          const pipeline = pipelinesWithId[idx];
          pipelineData.stages.forEach(stage => {
            (stage.deals || []).forEach(d => {
              allDeals.push({
                ...d,
                stageName: stage.name,
                pipelineId: d.pipelineId ?? pipeline?.id,
                pipelineName: pipeline?.name || pipelineData?.name || '-',
              });
            });
          });
        });

        const seen = new Set();
        const unique = allDeals.filter(d => {
          if (seen.has(d.id)) return false;
          seen.add(d.id);
          return true;
        });
        setContactDealsFromAllPipelines(
          unique.sort((a, b) => (a.id || 0) - (b.id || 0))
        );
      } catch (err) {
        console.warn(
          'Erro ao buscar deals do contato em todas as pipelines:',
          err
        );
        setContactDealsFromAllPipelines([]);
        setStagesForActivityHistory([]);
      } finally {
        setLoadingContactDealsAllPipelines(false);
      }
    },
    [findAllPipelines, findById]
  );

  // Lista de deals para o select (de outras pipelines ou do ticket)
  const contactDealsList = useMemo(() => {
    if (contactDealsFromAllPipelines.length > 0) {
      return contactDealsFromAllPipelines;
    }
    const deals = ticket?.deals || [];
    if (!deals.length) return [];
    const currentDeal = getCurrentDeal();
    const stage = currentDeal?.stage || ticket?.deal?.stage;
    const pipeline = currentDeal?.pipeline || ticket?.deal?.pipeline;
    return deals
      .sort((a, b) => (a.id || 0) - (b.id || 0))
      .map(d => ({
        ...d,
        stageName: d.stage?.name || stage?.name || getDealStatusLabel(d),
        pipelineId: d.pipelineId ?? pipeline?.id ?? d.pipeline?.id,
        pipelineName: d.pipeline?.name || pipeline?.name || '-',
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.deals, ticket?.deal, contactDealsFromAllPipelines]);

  const stagesForDealActivityHistory = stagesForActivityHistory;

  const currentDealId = getCurrentDeal()?.id;
  useEffect(() => {
    if (open && currentDealId) {
      setSelectedDealIdForHistory(currentDealId);
    }
  }, [open, currentDealId]);

  // Marcar como tendo alterações pendentes quando há checkboxes (para mostrar botão salvar)
  useEffect(() => {
    if (stageActivities.length > 0) {
      const hasCheckboxes = stageActivities.some(
        activity => activity.tipo_gatilho === 'checkbox'
      );
      if (hasCheckboxes && !hasUnsavedChanges) {
        // Marcar como tendo alterações pendentes para mostrar o botão salvar
        setHasUnsavedChanges(true);

        // Adicionar todos os checkboxes como alterações pendentes com valor false
        const checkboxChanges = {};
        stageActivities.forEach(activity => {
          if (activity.tipo_gatilho === 'checkbox') {
            checkboxChanges[activity.id] =
              activity.value !== undefined ? activity.value : false;
          }
        });
        setPendingChanges(checkboxChanges);
      }
    }
  }, [stageActivities, hasUnsavedChanges]);

  // Função para salvar uma atividade individual
  const handleSaveActivity = async (activityId, value) => {
    try {
      // Salvar atividade individual na API usando o deal ativo
      const currentDeal = getCurrentDeal();
      const dealId =
        currentDeal?.id ?? ticket?.dealId ?? ticket?.deal?.id ?? null;
      if (dealId) {
        await api.post(`/deals/${dealId}/requirement/${activityId}`, {
          value: value,
        });

        // Atualizar estado local das atividades
        setStageActivities(prev =>
          prev.map(activity =>
            activity.id === activityId
              ? { ...activity, value: value }
              : activity
          )
        );
      }
    } catch (err) {
      console.error(`Erro ao salvar atividade ${activityId}:`, err);
      toastError(err);
    }
  };

  // Função para lidar com mudanças no estado local (sem salvamento automático)
  const handleActivityChange = (activityId, value) => {
    // Encontrar a atividade para determinar o tipo
    const activity = stageActivities.find(a => a.id === activityId);

    // Processar valor baseado no tipo da atividade
    let processedValue = value;
    if (activity?.tipo_gatilho === 'checkbox') {
      // Para checkboxes, garantir que seja boolean
      processedValue = typeof value === 'boolean' ? value : Boolean(value);
    } else {
      // Para outros tipos (texto, select, etc.), manter o valor original
      processedValue = value;
    }

    // Atualizar estado local imediatamente
    setStageActivities(prev =>
      prev.map(activity =>
        activity.id === activityId
          ? { ...activity, value: processedValue }
          : activity
      )
    );

    // Marcar como alteração pendente
    setPendingChanges(prev => ({
      ...prev,
      [activityId]: processedValue,
    }));

    // Marcar que há alterações não salvas
    setHasUnsavedChanges(true);
  };

  // Função para salvar todas as alterações pendentes
  const handleSaveAllChanges = async () => {
    if (!hasUnsavedChanges || Object.keys(pendingChanges).length === 0) {
      return;
    }

    setLoading(true);
    try {
      // Salvar todas as alterações pendentes
      const savePromises = Object.entries(pendingChanges).map(
        ([activityId, value]) => handleSaveActivity(activityId, value)
      );

      await Promise.all(savePromises);

      // Limpar alterações pendentes
      setPendingChanges({});
      setHasUnsavedChanges(false);

      toast.success('Alterações salvas com sucesso!');

      if (typeof onActivitiesSaved === 'function') {
        Promise.resolve(onActivitiesSaved()).catch(err =>
          console.error(
            'Erro ao atualizar informações após salvar atividades:',
            err
          )
        );
      }

      // Fechar o modal após salvar
      onClose();
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  // Funções para abrir modais específicos
  const handleOpenOrderModal = () => {
    setOrderModalOpen(true);
  };

  const handleCloseOrderModal = async () => {
    setOrderModalOpen(false);
    // Recarregar dados das atividades e verificar conclusão do estágio
    if (getCurrentDeal()?.id ?? ticket?.dealId ?? ticket?.deal?.id) {
      await loadActivityResponses();
      await loadRequirementsWithFilling();
      await checkStageCompletion();
    }
  };

  const handleSaleSuccessWithServices = soldServiceProducts => {
    if (!soldServiceProducts?.length) return;
    setScheduleQueue(
      soldServiceProducts.flatMap(s =>
        Array(s.quantity || 1).fill().map(() => ({ ...s, quantity: 1 }))
      )
    );
    setScheduleIdToEdit(null);
    setTimeout(() => setGoogleScheduleModalOpen(true), 0);
  };

  const handleOpenGoogleScheduleModal = () => {
    setScheduleQueue([]);
    setScheduleIdToEdit(null);
    setGoogleScheduleModalOpen(true);
  };

  const handleOpenGoogleScheduleModalForEdit = calendarId => {
    setScheduleQueue([]);
    setScheduleIdToEdit(calendarId);
    setGoogleScheduleModalOpen(true);
  };

  const handleCloseGoogleScheduleModal = async result => {
    setGoogleScheduleModalOpen(false);

    if (result?.saved && scheduleQueue.length > 0) {
      const next = scheduleQueue.slice(1);
      setScheduleQueue(next);
      if (next.length > 0) {
        setTimeout(() => setGoogleScheduleModalOpen(true), 150);
      }
    }

    setScheduleIdToEdit(null);
    if (getCurrentDeal()?.id ?? ticket?.dealId ?? ticket?.deal?.id) {
      await loadActivityResponses();
      await loadRequirementsWithFilling();
      await checkStageCompletion();
    }
  };

  const handleOpenFileLinksModal = () => {
    const currentDeal = getCurrentDeal();
    const dealId =
      currentDeal?.id ?? ticket?.dealId ?? ticket?.deal?.id ?? null;
    if (!dealId) {
      toast.error('Nenhum negócio vinculado a este ticket.');
      return;
    }
    setFileLinksDealId(dealId);
    setFileLinksModalOpen(true);
  };

  const handleCloseFileLinksModal = async () => {
    setFileLinksModalOpen(false);
    setFileLinksDealId(null);
    // Recarregar dados das atividades após anexo
    if (getCurrentDeal()?.id ?? ticket?.dealId ?? ticket?.deal?.id) {
      await loadActivityResponses();
      await checkStageCompletion();
    }
  };

  // Função para verificar se o estágio foi concluído
  const checkStageCompletion = async () => {
    try {
      const currentDeal = getCurrentDeal();
      const dealId =
        currentDeal?.id ?? ticket?.dealId ?? ticket?.deal?.id ?? null;
      if (!dealId || !ticket?.stageId) {
        return;
      }

      // Buscar informações atualizadas do deal
      const { data: dealData } = await api.get(`/deals/${dealId}`);

      // Verificar se o estágio mudou (deal foi movido para outro estágio)
      if (dealData.stageId !== ticket.stageId) {
        setStageCompleted(true);
        toast.success(
          '🎉 Parabéns! O estágio foi concluído e o deal foi movido para o próximo estágio! O modal será fechado automaticamente.'
        );

        if (typeof onActivitiesSaved === 'function') {
          Promise.resolve(onActivitiesSaved()).catch(err =>
            console.error(
              'Erro ao atualizar informações após concluir estágio:',
              err
            )
          );
        }

        // Fechar o modal de atividades já que o deal não está mais neste estágio
        setTimeout(() => {
          onClose();
        }, 2000);

        return;
      }

      // Verificar se todas as atividades obrigatórias foram preenchidas
      const requiredActivities = stageActivities.filter(
        activity => activity.obrigatorio
      );
      const completedRequiredActivities = requiredActivities.filter(
        activity =>
          activity.filled &&
          activity.value !== '' &&
          activity.value !== null &&
          activity.value !== undefined
      );

      if (
        requiredActivities.length > 0 &&
        completedRequiredActivities.length === requiredActivities.length
      ) {
        toast.success('✅ Todas as atividades obrigatórias foram concluídas!');
      }
    } catch (error) {
      console.error('Erro ao verificar conclusão do estágio:', error);
      toastError(error);
    }
  };

  // Função para detectar se uma atividade é de um tipo específico
  const getActivityType = activity => {
    const description = activity.descricao?.toLowerCase() || '';

    if (
      description.includes('venda') ||
      description.includes('realizar venda') ||
      description.includes('vendas')
    ) {
      return 'vendas';
    }
    if (
      description.includes('agendamento') ||
      description.includes('agendar') ||
      description.includes('agenda')
    ) {
      return 'agendamento';
    }
    if (
      description.includes('anexo') ||
      description.includes('arquivo') ||
      description.includes('documento')
    ) {
      return 'anexos';
    }

    return 'default';
  };

  // Função para renderizar botões específicos de atividades
  const renderActivityButtons = activity => {
    const activityType = getActivityType(activity);
    switch (activityType) {
      case 'vendas':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenOrderModal}
              // Permitir atualizar requirement de venda mesmo após preenchido
            >
              Realizar Venda
            </Button>
          </Box>
        );

      case 'agendamento':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenGoogleScheduleModal}
              // Permitir atualizar requirement de agendamento mesmo após preenchido
            >
              Agendar
            </Button>
          </Box>
        );

      case 'anexos':
        return (
          <Box display='flex' gap={1} mt={1}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleOpenFileLinksModal}
              // Permitir atualizar requirement de anexo mesmo após preenchido
            >
              Anexar Arquivos
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  // Função para renderizar diferentes tipos de atividades
  const renderActivityInput = activity => {
    const value = activity.value || '';
    const isRequired = activity.obrigatorio;
    const isEmpty = !value || value === '';
    const hasError = isRequired && isEmpty;

    // Helper para adicionar asterisco em campos obrigatórios
    const getLabel = baseLabel => {
      return isRequired ? `${baseLabel} *` : baseLabel;
    };

    switch (activity.tipo_gatilho) {
      case 'checkbox':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={e =>
                    handleActivityChange(activity.id, e.target.checked)
                  }
                  color='primary'
                />
              }
              label={getLabel(activity.descricao)}
            />
            <Typography
              variant='caption'
              color='textSecondary'
              display='block'
              style={{ marginTop: 4 }}
            >
              {activity.obrigatorio
                ? 'Campo obrigatório - Marque o checkbox para a resposta "Sim" ou deixe desmarcado para "Não"'
                : 'Marque o checkbox para a resposta "Sim" ou deixe desmarcado para "Não"'}
            </Typography>
            {/* Indicador de atividade realizada */}
            {activity.filled && value && (
              <Box display='flex' alignItems='center' gap={1} mt={1}>
                <Checkbox
                  checked={true}
                  disabled={true}
                  color='primary'
                  style={{
                    color: '#4caf50',
                    cursor: 'default',
                  }}
                />
                <Typography variant='caption' color='primary'>
                  Atividade já realizada
                </Typography>
                <Chip
                  size='small'
                  label='Concluído'
                  color='primary'
                  variant='filled'
                  style={{ marginLeft: 'auto' }}
                />
              </Box>
            )}

            {/* Botões específicos para tipos de atividades */}
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'select':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <FormControl fullWidth error={hasError}>
              <InputLabel>{getLabel(activity.descricao)}</InputLabel>
              <Select
                value={value}
                onChange={e =>
                  handleActivityChange(activity.id, e.target.value)
                }
              >
                {activity.opcoes?.map((opcao, index) => (
                  <MenuItem key={index} value={opcao}>
                    {opcao}
                  </MenuItem>
                ))}
              </Select>
              {hasError && (
                <Typography variant='caption' color='error'>
                  Este campo é obrigatório
                </Typography>
              )}
              {/* Indicador de atividade realizada */}
              {activity.filled && value && (
                <Box display='flex' alignItems='center' gap={1} mt={1}>
                  <Checkbox
                    checked={true}
                    disabled={true}
                    color='primary'
                    style={{
                      color: '#4caf50',
                      cursor: 'default',
                    }}
                  />
                  <Typography variant='caption' color='primary'>
                    Atividade já realizada
                  </Typography>
                  <Chip
                    size='small'
                    label='Concluído'
                    color='primary'
                    variant='filled'
                    style={{ marginLeft: 'auto' }}
                  />
                </Box>
              )}
            </FormControl>

            {/* Botões específicos para tipos de atividades */}
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'date':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <TextField
              label={getLabel(activity.descricao)}
              type='date'
              value={value || ''}
              onChange={e => handleActivityChange(activity.id, e.target.value)}
              fullWidth
              error={hasError}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Limita a data máxima para hoje
                style: { cursor: 'text' }, // Cursor editável ao invés de pointer
              }}
              placeholder='Selecione uma data'
              InputProps={{
                startAdornment: (
                  <Box
                    component='span'
                    sx={{
                      color: theme.palette.text.secondary,
                      marginRight: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span role='img' aria-label='Calendário'>
                      📅
                    </span>
                  </Box>
                ),
              }}
            />
            {hasError && (
              <Typography variant='caption' color='error' display='block'>
                Este campo é obrigatório
              </Typography>
            )}
            {/* Indicador de atividade realizada */}
            {activity.filled && value && (
              <Box display='flex' alignItems='center' gap={1} mt={1}>
                <Checkbox
                  checked={true}
                  disabled={true}
                  color='primary'
                  style={{
                    color: '#4caf50',
                    cursor: 'default',
                  }}
                />
                <Typography variant='caption' color='primary'>
                  Atividade já realizada
                </Typography>
                <Chip
                  size='small'
                  label='Concluído'
                  color='primary'
                  variant='filled'
                  style={{ marginLeft: 'auto' }}
                />
              </Box>
            )}

            {/* Botões específicos para tipos de atividades */}
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'indicator':
        return (
          <Box key={activity.id} className={classes.activityField}>
            <Box display='flex' alignItems='center' gap={1}>
              <Checkbox
                checked={activity.filled}
                disabled={true}
                color='primary'
                style={{
                  color: activity.filled ? '#4caf50' : '#9e9e9e',
                  cursor: 'default',
                }}
              />
              <Typography variant='body2' color='textSecondary'>
                {activity.descricao}
              </Typography>
              <Chip
                size='small'
                label={activity.filled ? 'Executado' : 'Pendente'}
                color={activity.filled ? 'primary' : 'default'}
                variant={activity.filled ? 'filled' : 'outlined'}
                style={{ marginLeft: 'auto' }}
              />
            </Box>

            {/* Botões específicos para tipos de atividades */}
            {renderActivityButtons(activity)}
          </Box>
        );

      case 'input':
      default:
        const isMultiline =
          activity.descricao.toLowerCase().includes('observação') ||
          activity.descricao.toLowerCase().includes('necessidades') ||
          activity.descricao.toLowerCase().includes('comentário') ||
          activity.descricao.toLowerCase().includes('orçamento');

        return (
          <Box key={activity.id} className={classes.activityField}>
            <TextField
              label={getLabel(activity.descricao)}
              value={value}
              onChange={e => handleActivityChange(activity.id, e.target.value)}
              fullWidth
              error={hasError}
              multiline={isMultiline}
              rows={isMultiline ? 3 : 1}
              placeholder={isRequired ? 'Campo obrigatório' : 'Digite aqui...'}
            />
            {hasError && (
              <Typography variant='caption' color='error' display='block'>
                Este campo é obrigatório
              </Typography>
            )}
            {/* Indicador de atividade realizada */}
            {activity.filled && value && (
              <Box display='flex' alignItems='center' gap={1} mt={1}>
                <Checkbox
                  checked={true}
                  disabled={true}
                  color='primary'
                  style={{
                    color: '#4caf50',
                    cursor: 'default',
                  }}
                />
                <Typography variant='caption' color='primary'>
                  Atividade já realizada
                </Typography>
                <Chip
                  size='small'
                  label='Concluído'
                  color='primary'
                  variant='filled'
                  style={{ marginLeft: 'auto' }}
                />
              </Box>
            )}

            {/* Botões específicos para tipos de atividades */}
            {renderActivityButtons(activity)}
          </Box>
        );
    }
  };

  // Função para calcular progresso baseado nas atividades preenchidas
  const getProgress = () => {
    if (stageActivities.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const total = stageActivities.length;
    const completed = stageActivities.filter(activity => {
      // Para tipos indicator (sale, attach, schedule), verificar se filled = true
      if (activity.tipo_gatilho === 'indicator') {
        return activity.filled === true;
      }
      // Para outros tipos, verificar se filled = true OU se há valor
      return (
        activity.filled === true ||
        (activity.value && activity.value !== '' && activity.value !== false)
      );
    }).length;

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  };

  const progress = getProgress();

  // Na aba "Atividades realizadas", ao editar usar o activity de stageActivities para estado sincronizado
  const getActivityForEdit = activityId => {
    const fromStage = stageActivities.find(a => a.id === activityId);
    if (fromStage) return fromStage;
    return completedActivitiesList.find(a => a.id === activityId) || null;
  };

  // Formatar valor da atividade para exibição resumida
  const formatActivityValueDisplay = activity => {
    const v = activity.value;
    if (activity.tipo_gatilho === 'checkbox') {
      return v === true || v === 'true' ? 'Sim' : 'Não';
    }
    if (activity.tipo_gatilho === 'date' && v) {
      try {
        const d = new Date(v);
        return Number.isFinite(d.getTime())
          ? d.toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : String(v);
      } catch {
        return String(v);
      }
    }
    if (activity.tipo_gatilho === 'indicator') {
      return activity.descricao || 'Concluído';
    }
    return v != null && v !== '' ? String(v) : '-';
  };

  // Formatar data/hora do preenchimento para exibição
  const formatFillingDate = dateStr => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      return Number.isFinite(d.getTime())
        ? d.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : null;
    } catch {
      return null;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box className={classes.title}>
            <AssignmentIcon />
            <Typography variant='h6'>
              Atividades do Estágio
              {hasUnsavedChanges && (
                <Chip
                  label='Alterações não salvas'
                  color='warning'
                  size='small'
                  style={{ marginLeft: 8 }}
                />
              )}
              {stageCompleted && (
                <Chip
                  label='🎉 Estágio Concluído!'
                  color='primary'
                  size='small'
                  style={{
                    marginLeft: 8,
                    backgroundColor: '#4caf50',
                    color: 'white',
                  }}
                />
              )}
            </Typography>
            {stageData && (
              <Chip
                label={stageData.name}
                size='small'
                style={{ backgroundColor: stageData.color, color: 'white' }}
                className={classes.stageChip}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => {
                  setTabValue(newValue);
                  setEditingActivityId(null);
                  if (newValue === 1) {
                    loadRequirementsWithFilling();
                  }
                }}
                indicatorColor='primary'
                textColor='primary'
                style={{ marginBottom: 16 }}
              >
                <Tab label='Atividades do estágio' />
                <Tab
                  style={{ minWidth: 240 }}
                  label={
                    <span style={{ whiteSpace: 'nowrap' }}>
                      Editar atividades realizadas
                      {completedActivitiesList.length > 0 && (
                        <Chip
                          size='small'
                          label={completedActivitiesList.length}
                          style={{ marginLeft: 8, height: 20 }}
                        />
                      )}
                    </span>
                  }
                />
              </Tabs>

              {tabValue === 0 && (
                <>
                  {/* Indicador de progresso */}
                  <Box className={classes.progressContainer}>
                    <Typography variant='body1'>Progresso:</Typography>
                    <Typography variant='body1' color='primary'>
                      {progress.completed}/{progress.total} (
                      {progress.percentage}%)
                    </Typography>
                    <Chip
                      size='small'
                      label={`${stageActivities.length} atividade${stageActivities.length > 1 ? 's' : ''}`}
                      color='primary'
                      variant='outlined'
                      style={{ marginLeft: 8 }}
                    />
                    <Box ml={2}>
                      <CircularProgress
                        variant='determinate'
                        value={progress.percentage}
                        size={24}
                        style={{
                          color:
                            theme.palette.type === 'dark'
                              ? teal[400]
                              : theme.palette.primary.main,
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider />

                  {/* Atividades do estágio */}
                  {stageActivities.length === 0 ? (
                    <Typography
                      variant='body1'
                      color='textSecondary'
                      style={{ textAlign: 'center', marginTop: 20 }}
                    >
                      Nenhuma atividade configurada para este estágio.
                    </Typography>
                  ) : (
                    <Box style={{ marginTop: 16 }}>
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        style={{ marginBottom: 16 }}
                      >
                        Complete as atividades configuradas para este estágio.
                        Campos marcados com * são obrigatórios.
                      </Typography>

                      {stageActivities.map(activity =>
                        renderActivityInput(activity)
                      )}

                      {/* Informações sobre o estágio */}
                      <Box style={{ marginTop: 16, textAlign: 'center' }}>
                        <Typography variant='caption' color='textSecondary'>
                          Cada atividade é salva automaticamente ao ser
                          preenchida.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {tabValue === 1 && (
                <Box style={{ marginTop: 8 }}>
                  {loadingCompletedActivities ? (
                    <Box
                      className={classes.loadingContainer}
                      style={{ minHeight: 120 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : completedActivitiesList.length === 0 ? (
                    <Typography
                      variant='body1'
                      color='textSecondary'
                      style={{ textAlign: 'center', marginTop: 24 }}
                    >
                      Nenhuma atividade realizada ainda.
                    </Typography>
                  ) : (
                    <Box display='flex' flexDirection='column' gap={2}>
                      {completedActivitiesList.map(activity => {
                        const activityToRender =
                          editingActivityId === activity.id
                            ? getActivityForEdit(activity.id)
                            : activity;
                        if (!activityToRender) return null;
                        return (
                          <Box
                            key={activity.id}
                            className={classes.activityField}
                            style={{
                              borderLeft: `4px solid ${theme.palette.success?.main || '#4caf50'}`,
                            }}
                          >
                            {editingActivityId === activity.id ? (
                              <>
                                <Typography
                                  variant='subtitle2'
                                  color='primary'
                                  style={{ marginBottom: 12 }}
                                >
                                  Editando: {activityToRender.descricao}
                                </Typography>
                                {renderActivityInput(activityToRender)}
                                <Box
                                  display='flex'
                                  gap={1}
                                  justifyContent='flex-end'
                                  style={{ marginTop: 12 }}
                                >
                                  <Button
                                    size='small'
                                    onClick={() => setEditingActivityId(null)}
                                  >
                                    Cancelar
                                  </Button>
                                  {activity.type !== 'sale' &&
                                    activity.type !== 'schedule' &&
                                    activity.type !== 'attach' && (
                                    <Button
                                      size='small'
                                      variant='contained'
                                      color='primary'
                                      startIcon={<SaveIcon />}
                                      onClick={async () => {
                                        const valueToSave =
                                          pendingChanges[activity.id] !==
                                          undefined
                                            ? pendingChanges[activity.id]
                                            : activityToRender.value;
                                        await handleSaveActivity(
                                          activity.id,
                                          valueToSave
                                        );
                                        setEditingActivityId(null);
                                        await loadActivityResponses();
                                        await loadRequirementsWithFilling();
                                      }}
                                    >
                                      Salvar
                                    </Button>
                                  )}
                                </Box>
                              </>
                            ) : (
                              <Box>
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  justifyContent='space-between'
                                  flexWrap='wrap'
                                  gap={2}
                                >
                                  <Box flex={1} minWidth={0}>
                                    <Typography
                                      variant='body2'
                                      style={{ fontWeight: 500 }}
                                    >
                                      {activity.descricao}
                                    </Typography>
                                    <Box
                                      style={{
                                        marginTop: 8,
                                        padding: 12,
                                        backgroundColor:
                                          theme.palette.action?.hover ||
                                          (theme.palette.type === 'dark'
                                            ? 'rgba(255,255,255,0.08)'
                                            : 'rgba(0,0,0,0.04)'),
                                        color: theme.palette.text.primary,
                                        borderRadius:
                                          theme.shape?.borderRadius || 4,
                                        border: `1px solid ${theme.palette.divider}`,
                                      }}
                                    >
                                      {activity.type === 'sale' &&
                                      activity.saleItems?.length > 0 ? (
                                        <Box>
                                          <Typography
                                            variant='body2'
                                            style={{ fontWeight: 600, marginBottom: 8 }}
                                          >
                                            Itens da venda
                                          </Typography>
                                          {activity.saleItems.map((item, idx) => (
                                            <Box
                                              key={idx}
                                              style={{
                                                marginBottom: 6,
                                                paddingLeft: 8,
                                                borderLeft: `3px solid ${theme.palette.primary?.main || '#1976d2'}`,
                                              }}
                                            >
                                              <Typography variant='body2' style={{ fontWeight: 500 }}>
                                                {item.name || 'Produto'}
                                              </Typography>
                                              <Typography variant='caption' display='block' color='textSecondary'>
                                                Quantidade: {item.quantity ?? '-'} · Valor unit.: R${' '}
                                                {Number(item.unitValue ?? 0).toLocaleString('pt-BR', {
                                                  minimumFractionDigits: 2,
                                                })}{' '}
                                                · Subtotal: R${' '}
                                                {Number((item.quantity ?? 0) * (item.unitValue ?? 0)).toLocaleString(
                                                  'pt-BR',
                                                  { minimumFractionDigits: 2 }
                                                )}
                                              </Typography>
                                            </Box>
                                          ))}
                                          <Typography
                                            variant='body2'
                                            style={{ fontWeight: 600, marginTop: 8 }}
                                          >
                                            Total: R${' '}
                                            {Number(activity.value ?? 0).toLocaleString('pt-BR', {
                                              minimumFractionDigits: 2,
                                            })}
                                          </Typography>
                                        </Box>
                                      ) : activity.type === 'schedule' &&
                                        activity.schedules?.length > 0 ? (
                                        <Box>
                                          <Typography
                                            variant='body2'
                                            style={{ fontWeight: 600, marginBottom: 8 }}
                                          >
                                            Agendamentos
                                          </Typography>
                                          {activity.schedules.map(schedule => {
                                            const scheduleDate = schedule.startDateTime
                                              ? formatFillingDate(schedule.startDateTime)
                                              : '-';
                                            return (
                                              <Box
                                                key={schedule.id}
                                                display='flex'
                                                alignItems='center'
                                                justifyContent='space-between'
                                                style={{
                                                  marginBottom: 6,
                                                  paddingLeft: 8,
                                                  paddingRight: 8,
                                                  paddingTop: 6,
                                                  paddingBottom: 6,
                                                  borderLeft: `3px solid ${theme.palette.secondary?.main || '#9c27b0'}`,
                                                  backgroundColor:
                                                    theme.palette.type === 'dark'
                                                      ? 'rgba(255,255,255,0.04)'
                                                      : 'rgba(0,0,0,0.02)',
                                                  borderRadius: 4,
                                                }}
                                              >
                                                <Box>
                                                  <Typography variant='body2' style={{ fontWeight: 500 }}>
                                                    {schedule.productName || schedule.title || 'Agendamento'}
                                                  </Typography>
                                                  <Typography variant='caption' display='block' color='textSecondary'>
                                                    {schedule.title ? `${schedule.title} · ` : ''}
                                                    {scheduleDate}
                                                    {schedule.location ? ` · ${schedule.location}` : ''}
                                                  </Typography>
                                                </Box>
                                                <Button
                                                  size='small'
                                                  variant='outlined'
                                                  color='primary'
                                                  startIcon={<EditIcon />}
                                                  onClick={() =>
                                                    handleOpenGoogleScheduleModalForEdit(schedule.id)
                                                  }
                                                >
                                                  Editar
                                                </Button>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant='body2'
                                          style={{ fontFamily: 'monospace' }}
                                        >
                                          {formatActivityValueDisplay(activity)}
                                        </Typography>
                                      )}
                                      {activity.filling && (
                                        <Box
                                          mt={1}
                                          pt={1}
                                          style={{
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                          }}
                                        >
                                          {activity.filling.filledAt && (
                                            <Typography
                                              variant='caption'
                                              display='block'
                                              color='textSecondary'
                                            >
                                              Preenchido em:{' '}
                                              {formatFillingDate(
                                                activity.filling.filledAt
                                              ) || '-'}
                                            </Typography>
                                          )}
                                          {activity.filling.lastActivityBy && (
                                            <Typography
                                              variant='caption'
                                              display='block'
                                              color='textSecondary'
                                            >
                                              Última edição por{' '}
                                              {activity.filling.lastActivityBy
                                                .userName || '—'}{' '}
                                              em{' '}
                                              {formatFillingDate(
                                                activity.filling.lastActivityBy
                                                  .at
                                              ) || '-'}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  {activity.type !== 'schedule' && (
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      color='primary'
                                      startIcon={<EditIcon />}
                                      onClick={() =>
                                        setEditingActivityId(activity.id)
                                      }
                                      style={{
                                        alignSelf: 'center',
                                        marginLeft: 8,
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Histórico de Atendimentos apenas na aba "Atividades do estágio" */}
              {tabValue === 0 &&
                (getCurrentDeal()?.id ?? ticket?.dealId ?? ticket?.deal?.id) && (
                <Box
                  sx={{
                    mt: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      }}
                    >
                      Histórico de atendimentos
                      {loadingContactDealsAllPipelines && (
                        <Box
                          component='span'
                          sx={{ ml: 1, fontWeight: 400, fontSize: '0.75rem' }}
                        >
                          (carregando outras pipelines...)
                        </Box>
                      )}
                    </Typography>
                    {contactDealsFromAllPipelines.length === 0 &&
                      !loadingContactDealsAllPipelines &&
                      ticket?.contact?.id && (
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() =>
                            fetchContactDealsFromAllPipelines(ticket.contact.id)
                          }
                        >
                          Ver atendimentos de outras pipelines
                        </Button>
                      )}
                  </Box>

                  {contactDealsFromAllPipelines.length > 1 ? (
                    <>
                      <FormControl
                        variant='outlined'
                        size='small'
                        fullWidth
                        sx={{ minWidth: 280 }}
                      >
                        <InputLabel id='stage-activities-attendance-select-label'>
                          Selecione o atendimento
                        </InputLabel>
                        <Select
                          labelId='stage-activities-attendance-select-label'
                          value={
                            selectedDealIdForHistory ??
                            getCurrentDeal()?.id ??
                            ''
                          }
                          onChange={e =>
                            setSelectedDealIdForHistory(Number(e.target.value))
                          }
                          label='Selecione o atendimento'
                        >
                          {contactDealsList.map((deal, index) => {
                            const stageLabel =
                              deal.stageName ?? getDealStatusLabel(deal);
                            const dateStr = formatDealDateForHistory(deal);
                            const pipelinePart = deal.pipelineName
                              ? ` · ${deal.pipelineName}`
                              : '';
                            const isCurrent = deal.id === getCurrentDeal()?.id;
                            const currentTag = isCurrent ? ' (atual)' : '';
                            const label = `Atendimento ${index + 1} - ${dateStr ? `${stageLabel} (${dateStr})` : stageLabel}${pipelinePart}${currentTag}`;
                            return (
                              <MenuItem key={deal.id} value={deal.id}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <DealActivityHistory
                        dealId={
                          selectedDealIdForHistory ?? getCurrentDeal()?.id
                        }
                        stages={stagesForDealActivityHistory}
                      />
                    </>
                  ) : (
                    <DealActivityHistory
                      dealId={
                        selectedDealIdForHistory ??
                        getCurrentDeal()?.id ??
                        ticket?.dealId ??
                        ticket?.deal?.id
                      }
                      stages={stagesForDealActivityHistory}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setPendingChanges({});
              setHasUnsavedChanges(false);
              setStageCompleted(false);
              setTabValue(0);
              setEditingActivityId(null);
              onClose();
            }}
            color='primary'
          >
            Fechar
          </Button>
          {hasUnsavedChanges && (
            <Button
              onClick={handleSaveAllChanges}
              variant='contained'
              color='primary'
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modais específicos para atividades */}
      <OrderModal
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        contactId={ticket?.contact?.id}
        ticketId={ticket?.id}
        dealId={getCurrentDeal()?.id}
        onSaleSuccess={handleSaleSuccessWithServices}
        reload={async () => {
          await loadActivityResponses();
          await checkStageCompletion();
        }}
      />

      <GoogleScheduleModal
        open={googleScheduleModalOpen}
        onClose={handleCloseGoogleScheduleModal}
        scheduleId={scheduleIdToEdit}
        contactId={ticket?.contact?.id}
        ticket={ticket}
        dealId={getCurrentDeal()?.id ?? ticket?.dealId ?? ticket?.deal?.id}
        contact={ticket?.contact}
        initialProductToSchedule={scheduleQueue[0] || undefined}
        reload={async () => {
          await loadActivityResponses();
          await loadRequirementsWithFilling();
          await checkStageCompletion();
        }}
      />

      <FileLinksModal
        open={fileLinksModalOpen}
        onClose={handleCloseFileLinksModal}
        ticket={ticket}
        dealId={fileLinksDealId}
        reload={async () => {
          // Recarregar dados das atividades após anexo
          await loadActivityResponses();
          await checkStageCompletion();
        }}
      />
    </>
  );
};

export default StageActivitiesModal;
